import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, List, ListItem, ListItemText,
  ListItemSecondaryAction, IconButton, Switch, Paper, CircularProgress,
  Divider, Snackbar, Alert, Autocomplete, Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getCollection, addDocument, updateDocument, deleteDocument } from '../../services/firestoreService';
import { useBranch } from '../../context/BranchContext';

const DEFAULT_CLINICS = [
  'Centro Médico', 'Semiología', 'Introducción a la Medicina', 'Enfermería',
];

const ManageClinics = () => {
  const { isAdmin, activeBranchId, accessibleBranches } = useBranch();
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const load = async () => {
    setLoading(true);
    try {
      const clinicsData = isAdmin
        ? await getCollection('clinics')
        : (await getDocs(
            query(collection(db, 'clinics'), where('branchIds', 'array-contains', activeBranchId))
          )).docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems(clinicsData.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); setBranches(accessibleBranches); }, [accessibleBranches]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    if (items.find((i) => i.name.toLowerCase() === newName.trim().toLowerCase())) {
      setSnackbar({ open: true, message: 'Ya existe esa clínica', severity: 'warning' });
      return;
    }
    const selectedBranchIds = selectedBranches.map((b) => b.id);
    await addDocument('clinics', {
      name: newName.trim(),
      active: true,
      branchIds: isAdmin ? selectedBranchIds : (selectedBranchIds.length ? selectedBranchIds : [activeBranchId]),
    });
    setNewName('');
    setSelectedBranches([]);
    setSnackbar({ open: true, message: 'Clínica agregada', severity: 'success' });
    load();
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setNewName(item.name);
    const itemBranches = (item.branchIds || []).map((branchId) => branches.find((b) => b.id === branchId)).filter(Boolean);
    setSelectedBranches(itemBranches);
  };

  const handleSaveEdit = async () => {
    if (!newName.trim()) return;
    const duplicate = items.find((i) => i.name.toLowerCase() === newName.trim().toLowerCase() && i.id !== editingItem.id);
    if (duplicate) {
      setSnackbar({ open: true, message: 'Ya existe esa clínica', severity: 'warning' });
      return;
    }
    const selectedBranchIds = selectedBranches.map((b) => b.id);
    await updateDocument('clinics', editingItem.id, {
      name: newName.trim(),
      branchIds: isAdmin ? selectedBranchIds : (selectedBranchIds.length ? selectedBranchIds : [activeBranchId]),
    });
    setEditingItem(null);
    setNewName('');
    setSelectedBranches([]);
    setSnackbar({ open: true, message: 'Clínica actualizada', severity: 'success' });
    load();
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setNewName('');
    setSelectedBranches([]);
  };

  const handleToggle = async (item) => {
    await updateDocument('clinics', item.id, { active: !item.active });
    load();
  };

  const handleDelete = async (id) => {
    await deleteDocument('clinics', id);
    setSnackbar({ open: true, message: 'Clínica eliminada', severity: 'info' });
    load();
  };

  const handleInitDefaults = async () => {
    for (const name of DEFAULT_CLINICS) {
      if (!items.find((i) => i.name === name)) {
        await addDocument('clinics', { name, active: true, branchIds: isAdmin ? [] : [activeBranchId] });
      }
    }
    setSnackbar({ open: true, message: 'Clínicas por defecto creadas', severity: 'success' });
    load();
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Clínicas</Typography>
          <Typography variant="body2" color="text.secondary">Centro o servicio al que pertenece cada profesional</Typography>
        </Box>
        {items.length === 0 && (
          <Button variant="outlined" size="small" onClick={handleInitDefaults}>
            Inicializar valores por defecto
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          label={editingItem ? "Editar clínica" : "Nueva clínica"}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (editingItem ? handleSaveEdit() : handleAdd())}
          size="small"
          sx={{ flex: 1 }}
        />
        <Autocomplete
          multiple
          value={selectedBranches}
          onChange={(e, value) => setSelectedBranches(value)}
          options={branches}
          getOptionLabel={(b) => b.name}
          renderInput={(params) => <TextField {...params} label="Sucursales" size="small" sx={{ width: 250 }} />}
          noOptionsText="Sin sucursales"
          clearText="Limpiar"
        />
        {editingItem ? (
          <>
            <Button variant="contained" onClick={handleSaveEdit} disabled={!newName.trim()}>
              Guardar
            </Button>
            <Button variant="outlined" onClick={handleCancelEdit}>
              Cancelar
            </Button>
          </>
        ) : (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} disabled={!newName.trim()}>
            Agregar
          </Button>
        )}
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {items.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No hay clínicas configuradas</Typography>
          </Box>
        ) : (
          <List dense>
            {items.map((item, idx) => (
              <React.Fragment key={item.id}>
                {idx > 0 && <Divider />}
                <ListItem>
                  <Switch size="small" checked={!!item.active} onChange={() => handleToggle(item)} sx={{ mr: 1 }} />
                  <ListItemText
                    primary={item.name}
                    secondary={
                      <>
                        {item.active ? 'Activa' : 'Inactiva'}
                        {item.branchIds && item.branchIds.length > 0 && (
                          <>
                            <br />
                            <span style={{ fontSize: '0.8rem', color: '#666' }}>
                              Sucursales: {item.branchIds.map((branchId) => branches.find((b) => b.id === branchId)?.name || branchId).join(', ')}
                            </span>
                          </>
                        )}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Editar">
                      <IconButton edge="end" size="small" onClick={() => handleEdit(item)} sx={{ mr: 1 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton edge="end" color="error" size="small" onClick={() => handleDelete(item.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageClinics;
